import { APITypes } from '../../primary-record-types';
import { ApiError } from 'aws-amplify/api';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
    getClientRecordGroupsCacheKey,
    getClientRecordInvitationCacheKey,
    getOrganizationClientRecordCacheKey,
    getOrganizationClientRecordHistoryCacheKey,
    getOrganizationClientRecordsBulkCacheKey,
    getOrganizationClientRecordsCacheKey,
    longTermCacheStaleTime,
    onInviteClientRecord,
    onUBulkUpdateOrganizationClientRecordGroups,
    onUpdateOrganizationClientRecordGroups,
    onUpdateOrganizationClientRecords,
} from './cacheKeys';
import { appAPI } from '../../utils/api';
import urls from '../../utils/urls';
import { useQueryErrorNotify } from '../useQueryCallbacks';
import _ from 'lodash';
import { getIntl } from '../../utils/intl';
import useNotify from '../useNotify';
import { useOrganizationId } from './useOrganization';
import _chunk from 'lodash/chunk';
import { useState } from 'react';

const intl = getIntl('useOrganizationClientRecords', {
    createClientRecordSuccess: 'Client record created successfully',
    createClientRecordError: 'Error creating client record',
    createClientRecordInvalidEmailError: 'Cannot use organization user email for point of contact',
    updateClientRecordSuccess: 'Client record updated successfully',
    updateClientRecordError: 'Error updating client record',
    loadClientRecordsError: 'Error loading client records',
    bulkLoadClientRecordsError: 'Error loading client records',
    errorLoadingClientRecord: 'Error loading client record',
    errorLoadingClientRecords: 'Error loading client records',
    reEngageClientRecordSuccess: 'Client record re-engaged successfully',
    reEngageClientRecordError: 'Error re-engaging client record',
    inviteClientRecordSuccess: 'Client invited successfully',
    inviteClientRecordError: 'Error inviting client',
    bulkInviteClientRecordsSuccess: 'Successfully sent invites',
    bulkInviteClientPartialSuccess: 'Sent {success} invites, {error} failed',
    bulkInviteClientRecordsError: 'Error inviting clients',
    errorLoadingClientRecordInvitation: 'Error loading client invitation',
    archiveClientRecordSuccess: 'Client successfully archived',
    archiveClientRecordError: 'Error archiving client',
    archiveClientRecordsError: 'Error archiving client',
    archiveClientRecordsSuccess: 'Clients successfully archived',
    unarchiveClientRecordSuccess: 'Client successfully unarchived',
    unarchiveClientRecordError: 'Error unarchiving client',
    errorLoadingClientRecordHistory: 'Error loading client record history',
    deleteClientRecordSuccess: 'Client{plural} deleted successfully',
    deleteClientRecordError: 'Error deleting client{plural}',
    bulkAddClientRecordsSuccess: 'Clients added successfully',
    bulkAddClientRecordsError: 'Error adding client',
    loadClientRecordGroupsError: 'Error loading client record groups',
    updateOrganizationClientRecordGroupSuccess: 'Organization client groups updated',
    updateOrganizationClientRecordGroupFailure: 'Failed to update organization client group',
    bulkAddClientGroupsSuccess: 'Successfully added clients to groups',
    bulkAddClientGroupsFailure: 'Failed to add client profiles groups',
});

export type UseOrganizationClientRecordHistoryArgs = {
    /** Null leaves the query idle, so screens shared with consumers can call this without an organization. */
    organizationId: string | null;
    medicalProfileId: string | null;
};

export type UseOrganizationClientRecordHistoryReturnValue = {
    clientRecordHistory: APITypes.organizationService.GetOrganizationClientRecordsForMedicalProfileResponse['items'];
    isLoadingClientRecordHistory: boolean;
};

/**
 * The organization's client records for a medical profile, past and present. Unlike useOrganizationClientRecords
 * this does not require a loaded organization: the query only runs once both ids are known.
 */
export const useOrganizationClientRecordHistory = ({
    organizationId,
    medicalProfileId,
}: UseOrganizationClientRecordHistoryArgs): UseOrganizationClientRecordHistoryReturnValue => {
    const organizationClientRecordHistoryQuery = useQuery({
        queryKey: medicalProfileId ? getOrganizationClientRecordHistoryCacheKey(medicalProfileId) : [],
        queryFn: async () => {
            if (!organizationId || !medicalProfileId) {
                throw new Error('Cannot get client record history without an organization and a profile');
            }
            return appAPI.get<APITypes.organizationService.GetOrganizationClientRecordsForMedicalProfileResponse>(
                urls.api.organizations.clientRecords.clientRecordHistory(organizationId, medicalProfileId),
            );
        },
        enabled: !!organizationId && !!medicalProfileId,
    });
    useQueryErrorNotify(organizationClientRecordHistoryQuery, intl('errorLoadingClientRecordHistory'));

    return {
        clientRecordHistory: organizationClientRecordHistoryQuery.data?.items || [],
        isLoadingClientRecordHistory: organizationClientRecordHistoryQuery.isLoading,
    };
};

/**
 * The record the organization currently holds for a profile: the active record, or an invitation whose provisioned
 * profile is this profile. Null when there is none; a past, inactive record does not count.
 */
export const getCurrentOrganizationClientRecordForProfile = (
    clientRecordHistory: UseOrganizationClientRecordHistoryReturnValue['clientRecordHistory'],
    medicalProfileId: string,
): APITypes.organizationService.OrganizationClientRecordDetails | null =>
    clientRecordHistory.find((record) => record.status === 'active') ??
    clientRecordHistory.find(
        (record) =>
            record.status === 'invited' &&
            'provisionedMedicalProfileId' in record &&
            record.provisionedMedicalProfileId === medicalProfileId,
    ) ??
    null;

export type UseOrganizationClientRecordsArgs = {
    onCreateClientRecordSuccess?: () => void;
    onUpdateClientRecordSuccess?: () => void;
    onReEngageClientRecordSuccess?: () => void;
    onInviteClientRecordSuccess?: () => void;
    onArchiveClientRecordSuccess?: () => void;
    onUnarchiveClientRecordSuccess?: () => void;
    onDeleteClientRecordsSuccess?: () => void;
    onArchiveClientRecordsSuccess?: () => void;
    onBulkAddClientRecordsSuccess?: () => void;
    onBulkInviteClientRecordsComplete?: () => void;
    onUpdateOrganizationClientRecordGroupsSuccess?: () => void;
    onBulkAddClientRecordsToGroupsSuccess?: () => void;
    organizationClientRecordId?: string;
    bulkLoadOrganizationClientRecordIds?: string[];
    loadClientRecordInvitation?: boolean;
    clientRecordHistoryProfileId?: string;
};

export type UpdateClientRecordArgs = {
    organizationClientRecordId: string;
    update: APITypes.organizationService.UpdateOrganizationClientRecordRequest;
};

export type InviteClientRecordArgs = {
    request: APITypes.organizationService.CreateOrganizationClientInvitationForOrganizationClientRecordRequest;
    organizationClientRecordId: string;
};

export type OrganizationClientRecordGroupDetails = {
    pastOrganizationClientRecords: APITypes.organizationService.OrganizationClientRecordDetails[];
    organizationGroups?: APITypes.organizationService.OrganizationGroupDetails[];
};

export type UpdateClientRecordGroupsArgs = {
    organizationGroupIds: string[];
    clientRecordId: string;
};

export type WithClientGroupDetails<TBaseType> = TBaseType & OrganizationClientRecordGroupDetails;

const emptyArray: any[] = [];

export type UseOrganizationClientRecordsReturnValue = {
    clientRecords: APITypes.organizationService.ListOrganizationClientRecordsResponse['items'];
    isLoadingClientRecords: boolean;
    isFetchingClientRecords: boolean;
    clientRecord: APITypes.organizationService.GetOrganizationClientRecordResponse | null;
    isLoadingClientRecord: boolean;
    isBulkLoadingClientRecords: boolean;
    bulkLoadedClientRecords: APITypes.organizationService.GetOrganizationClientRecordResponse[];
    createClientRecord: (clientDetails: APITypes.organizationService.CreateOrganizationClientRecordRequest) => void;
    isCreatingClientRecord: boolean;
    updateClientRecord: (args: UpdateClientRecordArgs) => void;
    isUpdatingClientRecord: boolean;
    reEngageClientRecord: (organizationClientRecordId: string) => void;
    isReEngagingClientRecord: boolean;
    inviteClientRecord: (args: InviteClientRecordArgs) => void;
    isInvitingClientRecord: boolean;
    bulkInviteClientRecords: (args: InviteClientRecordArgs[]) => void;
    bulkInviteClientRecordProgress: number;
    isBulkInvitingClientRecords: boolean;
    clientRecordInvitation: APITypes.organizationService.GetOrganizationClientInvitationForOrganizationClientRecordResponse | null;
    isLoadingClientRecordInvitation: boolean;
    archiveClientRecord: (organizationClientRecordId: string) => void;
    isArchivingClientRecord: boolean;
    archiveClientRecords: (organizationClientRecordIds: string[]) => void;
    isArchivingClientRecords: boolean;
    unarchiveClientRecord: (organizationClientRecordId: string) => void;
    isUnarchivingClientRecord: boolean;
    clientRecordHistory: APITypes.organizationService.GetOrganizationClientRecordsForMedicalProfileResponse['items'];
    isLoadingClientRecordHistory: boolean;
    deleteClientRecords: (organizationClientRecordIds: string[]) => void;
    isDeletingClientRecords: boolean;
    bulkAddClientRecords: (records: APITypes.organizationService.BulkCreateOrganizationClientRecordsRequest) => void;
    isBulkAddingClientRecords: boolean;
    clientRecordGroups: APITypes.organizationService.ListOrganizationClientRecordOrganizationGroupsResponse['items'];
    isLoadingClientRecordGroups: boolean;
    updateOrganizationClientRecordGroups: (args: UpdateClientRecordGroupsArgs) => void;
    isUpdatingOrganizationClientRecordGroups: boolean;
    bulkAddClientRecordsToGroups: (
        args: APITypes.organizationService.BulkAddOrganizationGroupClientRecordsRequest,
    ) => void;
    isBulkAddingClientRecordsToGroups: boolean;
};

const isOrganizationUserEmailError = (error: Error) =>
    error instanceof ApiError && error.response?.body === 'Cannot set organization user as client organizer';

export const useOrganizationClientRecords = ({
    onCreateClientRecordSuccess,
    onBulkAddClientRecordsSuccess,
    onUpdateClientRecordSuccess,
    onReEngageClientRecordSuccess,
    onInviteClientRecordSuccess,
    onArchiveClientRecordSuccess,
    onArchiveClientRecordsSuccess,
    onUnarchiveClientRecordSuccess,
    onDeleteClientRecordsSuccess,
    onBulkInviteClientRecordsComplete,
    onUpdateOrganizationClientRecordGroupsSuccess,
    onBulkAddClientRecordsToGroupsSuccess,
    organizationClientRecordId,
    loadClientRecordInvitation,
    clientRecordHistoryProfileId,
    bulkLoadOrganizationClientRecordIds,
}: UseOrganizationClientRecordsArgs): UseOrganizationClientRecordsReturnValue => {
    const notify = useNotify();
    const organizationId = useOrganizationId();
    const [bulkInviteClientRecordProgress, setBulkInviteClientRecordProgress] = useState(0);

    const organizationClientRecordsQuery = useQuery({
        queryKey: getOrganizationClientRecordsCacheKey(),
        queryFn: async () => {
            return appAPI.get<APITypes.organizationService.ListOrganizationClientRecordsResponse>(
                urls.api.organizations.clientRecords.clientRecords(organizationId),
            );
        },
        staleTime: longTermCacheStaleTime,
    });
    useQueryErrorNotify(organizationClientRecordsQuery, intl('loadClientRecordsError'));

    const organizationClientRecordQuery = useQuery({
        queryKey: getOrganizationClientRecordCacheKey(organizationClientRecordId || '*'),
        queryFn: async () => {
            if (!organizationClientRecordId) {
                throw new Error('Cannot get client record without a client record id');
            }
            return appAPI.get<APITypes.organizationService.GetOrganizationClientRecordResponse>(
                urls.api.organizations.clientRecords.clientRecord(organizationId, organizationClientRecordId),
            );
        },
        enabled: !!organizationClientRecordId,
    });
    useQueryErrorNotify(organizationClientRecordQuery, intl('errorLoadingClientRecord'));

    const organizationClientRecordGroupsQuery = useQuery({
        queryKey: getClientRecordGroupsCacheKey(organizationClientRecordId || '*'),
        queryFn: async () => {
            return appAPI.get<APITypes.organizationService.ListOrganizationClientRecordOrganizationGroupsResponse>(
                urls.api.organizations.clientRecords.clientRecordGroups(
                    organizationId,
                    organizationClientRecordId || '*',
                ),
            );
        },
        enabled: !!organizationClientRecordId,
    });
    useQueryErrorNotify(organizationClientRecordGroupsQuery, intl('loadClientRecordGroupsError'));

    const updateOrganizationClientRecordGroups = useMutation({
        mutationFn: async (args: UpdateClientRecordGroupsArgs) => {
            const body: APITypes.organizationService.UpdateOrganizationClientRecordOrganizationGroupsRequest = {
                organizationGroupIds: args.organizationGroupIds,
            };
            return appAPI.put<APITypes.organizationService.UpdateOrganizationClientRecordResponse>(
                urls.api.organizations.clientRecords.clientRecordGroups(organizationId, args.clientRecordId),
                {
                    body,
                },
            );
        },
        onSuccess: (_data, args) => {
            onUpdateOrganizationClientRecords.resetCache({})();
            notify('success', intl('updateOrganizationClientRecordGroupSuccess'));
            onUpdateOrganizationClientRecordGroups.resetCache({ clientRecordId: args.clientRecordId });
            onUpdateOrganizationClientRecordGroupsSuccess?.();
        },
        onError: () => {
            notify('error', intl('updateOrganizationClientRecordGroupFailure'));
        },
    });

    const bulkLoadOrganizationClientRecordsQuery = useQuery({
        queryKey: getOrganizationClientRecordsBulkCacheKey(bulkLoadOrganizationClientRecordIds || []),
        queryFn: async () => {
            if (!bulkLoadOrganizationClientRecordIds?.length) {
                throw new Error('Cannot bulk load client records without client record ids');
            }
            return Promise.all(
                bulkLoadOrganizationClientRecordIds.map((_) =>
                    appAPI.get<APITypes.organizationService.GetOrganizationClientRecordResponse>(
                        urls.api.organizations.clientRecords.clientRecord(organizationId, _),
                    ),
                ),
            );
        },
        enabled: !!bulkLoadOrganizationClientRecordIds?.length,
    });
    useQueryErrorNotify(bulkLoadOrganizationClientRecordsQuery, intl('bulkLoadClientRecordsError'));

    const createClientRecordMutation = useMutation({
        mutationFn: async (clientDetails: APITypes.organizationService.CreateOrganizationClientRecordRequest) => {
            return appAPI.post<APITypes.organizationService.CreateOrganizationClientRecordResponse>(
                urls.api.organizations.clientRecords.clientRecords(organizationId),
                {
                    body: clientDetails,
                },
            );
        },
        onSuccess: () => {
            onCreateClientRecordSuccess?.();
            notify('success', intl('createClientRecordSuccess'));
            onUpdateOrganizationClientRecords.resetCache({})();
        },
        onError: (error) => {
            if (isOrganizationUserEmailError(error)) notify('error', intl('createClientRecordInvalidEmailError'));
            else notify('error', intl('createClientRecordError'));
        },
    });

    const bulkAddClientRecordsMutation = useMutation({
        mutationFn: async (records: APITypes.organizationService.BulkCreateOrganizationClientRecordsRequest) => {
            const chunkedRecords = _chunk(records.organizationClientRecords, 200);
            for (const chunk of chunkedRecords) {
                await appAPI.post<APITypes.organizationService.BulkCreateOrganizationClientRecordsResponse>(
                    urls.api.organizations.clientRecords.bulkAdd(organizationId),
                    {
                        body: { organizationClientRecords: chunk },
                    },
                );
            }
        },
        onSuccess: () => {
            onBulkAddClientRecordsSuccess?.();
            notify('success', intl('bulkAddClientRecordsSuccess'));
            onUpdateOrganizationClientRecords.resetCache({})();
        },
        onError: () => {
            notify('error', intl('bulkAddClientRecordsError'));
        },
    });

    const updateClientRecordMutation = useMutation({
        mutationFn: async (args: UpdateClientRecordArgs) => {
            return appAPI.put<APITypes.organizationService.UpdateOrganizationClientRecordResponse>(
                urls.api.organizations.clientRecords.clientRecord(organizationId, args.organizationClientRecordId),
                {
                    body: args.update,
                },
            );
        },
        onSuccess: () => {
            onUpdateClientRecordSuccess?.();
            notify('success', intl('updateClientRecordSuccess'));
            onUpdateOrganizationClientRecords.resetCache({})();
        },
        onError: () => {
            notify('error', intl('updateClientRecordError'));
        },
    });

    const reEngageClientRecordMutation = useMutation({
        mutationFn: async (organizationClientRecordId: string) => {
            return appAPI.post<APITypes.organizationService.UpdateOrganizationClientRecordResponse>(
                urls.api.organizations.clientRecords.reEngageClient(organizationId, organizationClientRecordId),
            );
        },
        onSuccess: () => {
            onReEngageClientRecordSuccess?.();
            notify('success', intl('reEngageClientRecordSuccess'));
            onUpdateOrganizationClientRecords.resetCache({})();
        },
        onError: () => {
            notify('error', intl('reEngageClientRecordError'));
        },
    });

    const inviteClientRecordMutation = useMutation({
        mutationFn: async (args: InviteClientRecordArgs) => {
            return appAPI.post<APITypes.organizationService.CreateOrganizationClientInvitationForOrganizationClientRecordResponse>(
                urls.api.organizations.clientRecords.inviteClient(organizationId, args.organizationClientRecordId),
                {
                    body: args.request,
                },
            );
        },
        onSuccess: () => {
            onInviteClientRecordSuccess?.();
            notify('success', intl('inviteClientRecordSuccess'));
            onInviteClientRecord.resetCache({})();
        },
        onError: (error) => {
            if (isOrganizationUserEmailError(error)) notify('error', intl('createClientRecordInvalidEmailError'));
            else notify('error', intl('createClientRecordError'));
        },
    });

    const bulkInviteClientRecordMutation = useMutation({
        mutationFn: async (args: InviteClientRecordArgs[]) => {
            const makeSingleRequest = async (request: InviteClientRecordArgs) => {
                await appAPI.post<APITypes.organizationService.CreateOrganizationClientInvitationForOrganizationClientRecordResponse>(
                    urls.api.organizations.clientRecords.inviteClient(
                        organizationId,
                        request.organizationClientRecordId,
                    ),
                    {
                        body: request.request,
                    },
                );
            };

            setBulkInviteClientRecordProgress(0);
            const result = { success: 0, error: 0 };
            for (let i = 0; i < args.length; i++) {
                try {
                    await makeSingleRequest(args[i]);
                    result.success++;
                } catch (e) {
                    result.error++;
                }
                setBulkInviteClientRecordProgress(((i + 1) / args.length) * 100);
            }

            return result;
        },
        onSuccess: (data) => {
            if (data.error === 0) {
                notify('success', intl('bulkInviteClientRecordsSuccess'));
            } else {
                notify('error', intl('bulkInviteClientPartialSuccess', { success: data.success, error: data.error }));
            }
            onInviteClientRecord.resetCache({})();
            onBulkInviteClientRecordsComplete?.();
        },
        onError: (error) => {
            if (isOrganizationUserEmailError(error)) notify('error', intl('createClientRecordInvalidEmailError'));
            else notify('error', intl('createClientRecordError'));

            onBulkInviteClientRecordsComplete?.();
        },
    });

    const organizationClientRecordInvitationQuery = useQuery({
        queryKey: organizationClientRecordId ? getClientRecordInvitationCacheKey(organizationClientRecordId) : [],
        queryFn: async () => {
            if (!organizationClientRecordId) {
                throw new Error('Cannot get client record invitation without a client record id');
            }
            return appAPI.get<APITypes.organizationService.GetOrganizationClientInvitationForOrganizationClientRecordResponse>(
                urls.api.organizations.clientRecords.clientRecordInvitation(organizationId, organizationClientRecordId),
            );
        },
        enabled: !!organizationClientRecordId && !!loadClientRecordInvitation,
    });
    useQueryErrorNotify(organizationClientRecordInvitationQuery, intl('errorLoadingClientRecordInvitation'));

    const archiveClientRecordMutation = useMutation({
        mutationFn: async (organizationClientRecordId: string) => {
            const body: APITypes.organizationService.UpdateOrganizationClientRecordRequest = {
                status: 'archived',
            };
            return appAPI.put(
                urls.api.organizations.clientRecords.clientRecord(organizationId, organizationClientRecordId),
                { body },
            );
        },
        onSuccess: () => {
            onArchiveClientRecordSuccess?.();
            notify('success', intl('archiveClientRecordSuccess'));
            onUpdateOrganizationClientRecords.resetCache({})();
        },
        onError: () => {
            notify('error', intl('archiveClientRecordError'));
        },
    });

    const archiveClientRecordsMutation = useMutation({
        mutationFn: async (organizationClientRecordIds: string[]) => {
            return Promise.all(
                organizationClientRecordIds.map((organizationClientRecordId) => {
                    const body: APITypes.organizationService.UpdateOrganizationClientRecordRequest = {
                        status: 'archived',
                    };
                    return appAPI.put(
                        urls.api.organizations.clientRecords.clientRecord(organizationId, organizationClientRecordId),
                        { body },
                    );
                }),
            );
        },
        onSuccess: () => {
            onArchiveClientRecordsSuccess?.();
            notify('success', intl('archiveClientRecordsSuccess'));
            onUpdateOrganizationClientRecords.resetCache({})();
        },
        onError: () => {
            notify('error', intl('archiveClientRecordsError'));
        },
    });

    const unarchiveClientRecordMutation = useMutation({
        mutationFn: async (organizationClientRecordId: string) => {
            const body: APITypes.organizationService.UpdateOrganizationClientRecordRequest = {
                status: 'not-invited',
            };
            return appAPI.put(
                urls.api.organizations.clientRecords.clientRecord(organizationId, organizationClientRecordId),
                { body },
            );
        },
        onSuccess: () => {
            onUnarchiveClientRecordSuccess?.();
            notify('success', intl('unarchiveClientRecordSuccess'));
            onUpdateOrganizationClientRecords.resetCache({})();
        },
        onError: () => {
            notify('error', intl('unarchiveClientRecordError'));
        },
    });

    const deleteClientRecordsMutation = useMutation({
        mutationFn: async (organizationClientRecordIds: string[]) => {
            return Promise.all(
                organizationClientRecordIds.map((organizationClientRecordId) =>
                    appAPI.del(
                        urls.api.organizations.clientRecords.clientRecord(organizationId, organizationClientRecordId),
                    ),
                ),
            );
        },
        onSuccess: (_res, input) => {
            notify('success', intl('deleteClientRecordSuccess', { plural: input.length > 1 ? 's' : '' }));
            onUpdateOrganizationClientRecords.resetCache({})();
            onDeleteClientRecordsSuccess?.();
        },
        onError: (_res, input) => {
            notify('error', intl('deleteClientRecordError', { plural: input.length > 1 ? 's' : '' }));
        },
    });

    const { clientRecordHistory, isLoadingClientRecordHistory } = useOrganizationClientRecordHistory({
        organizationId,
        medicalProfileId: clientRecordHistoryProfileId || null,
    });

    const bulkAddClientRecordsToGroupsMutation = useMutation({
        mutationFn: async (args: APITypes.organizationService.BulkAddOrganizationGroupClientRecordsRequest) => {
            return appAPI.post(urls.api.organizations.groups.bulkAddClientRecordGroups(organizationId), {
                body: args,
            });
        },
        onSuccess: (_data, args) => {
            notify('success', intl('bulkAddClientGroupsSuccess'));
            onUBulkUpdateOrganizationClientRecordGroups.resetCache({
                clientRecordIds: args.organizationClientRecordIds,
            })();
            onBulkAddClientRecordsToGroupsSuccess?.();
        },
        onError: () => {
            notify('error', intl('bulkAddClientGroupsFailure'));
        },
    });

    return {
        clientRecords: organizationClientRecordsQuery.data?.items || [],
        isLoadingClientRecords: organizationClientRecordsQuery.isLoading,
        isFetchingClientRecords: organizationClientRecordsQuery.isFetching,
        clientRecord: organizationClientRecordQuery.data || null,
        isLoadingClientRecord: organizationClientRecordQuery.isLoading,
        createClientRecord: createClientRecordMutation.mutate,
        isCreatingClientRecord: createClientRecordMutation.isPending,
        updateClientRecord: updateClientRecordMutation.mutate,
        isUpdatingClientRecord: updateClientRecordMutation.isPending,
        reEngageClientRecord: reEngageClientRecordMutation.mutate,
        isReEngagingClientRecord: reEngageClientRecordMutation.isPending,
        inviteClientRecord: inviteClientRecordMutation.mutate,
        isInvitingClientRecord: inviteClientRecordMutation.isPending,
        clientRecordInvitation: organizationClientRecordInvitationQuery.data || null,
        isLoadingClientRecordInvitation: organizationClientRecordInvitationQuery.isLoading,
        archiveClientRecord: archiveClientRecordMutation.mutate,
        isArchivingClientRecord: archiveClientRecordMutation.isPending,
        unarchiveClientRecord: unarchiveClientRecordMutation.mutate,
        isUnarchivingClientRecord: unarchiveClientRecordMutation.isPending,
        clientRecordHistory,
        isLoadingClientRecordHistory,
        deleteClientRecords: deleteClientRecordsMutation.mutate,
        isDeletingClientRecords: deleteClientRecordsMutation.isPending,
        bulkAddClientRecords: bulkAddClientRecordsMutation.mutate,
        isBulkAddingClientRecords: bulkAddClientRecordsMutation.isPending,
        isBulkLoadingClientRecords: bulkLoadOrganizationClientRecordsQuery.isLoading,
        bulkLoadedClientRecords: bulkLoadOrganizationClientRecordsQuery.data || [],
        bulkInviteClientRecords: bulkInviteClientRecordMutation.mutate,
        bulkInviteClientRecordProgress,
        isBulkInvitingClientRecords: bulkInviteClientRecordMutation.isPending,
        clientRecordGroups: organizationClientRecordGroupsQuery.data?.items || emptyArray,
        isLoadingClientRecordGroups: organizationClientRecordGroupsQuery.isLoading,
        updateOrganizationClientRecordGroups: updateOrganizationClientRecordGroups.mutate,
        isUpdatingOrganizationClientRecordGroups: updateOrganizationClientRecordGroups.isPending,
        bulkAddClientRecordsToGroups: bulkAddClientRecordsToGroupsMutation.mutate,
        isBulkAddingClientRecordsToGroups: bulkAddClientRecordsToGroupsMutation.isPending,
        archiveClientRecords: archiveClientRecordsMutation.mutate,
        isArchivingClientRecords: archiveClientRecordsMutation.isPending,
    };
};
